import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout, Menu, Button, Typography, Space, Switch,
  ConfigProvider, theme, Drawer, Grid,
} from 'antd';
import {
  MenuUnfoldOutlined, MenuFoldOutlined, DashboardOutlined,
  SettingOutlined, LogoutOutlined, AppstoreOutlined,
  UserOutlined, MenuOutlined, HomeOutlined, PlusOutlined,
} from '@ant-design/icons';
import { ReactComponent as CowfaceIcon } from '../Iconos/Cowface.svg';
import { useUser, usePermisos } from '../context/UserContext';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { defaultAlgorithm, darkAlgorithm } = theme;
const { useBreakpoint } = Grid;

// ─── Menú completo (reutilizado en sidebar y drawer) ───────────────────────

function MenuItems({ onSelect, darkMode }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { logout } = useUser();
  const { puedeVer } = usePermisos();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Menu
      theme={darkMode ? 'dark' : 'light'}
      mode="inline"
      selectedKeys={[pathname]}
      defaultOpenKeys={['/dashboard/inventario', '/dashboard/ganado', '/dashboard/cultivos', '/dashboard/admin']}
      onClick={onSelect}
    >
      <Menu.Item key="/dashboard" icon={<DashboardOutlined />}>
        <Link to="/dashboard">Inicio</Link>
      </Menu.Item>

      {puedeVer('inventario') && (
        <Menu.SubMenu key="/dashboard/inventario" icon={<AppstoreOutlined />} title="Inventario">
          <Menu.Item key="/dashboard/FrminventarioTable">
            <Link to="/dashboard/FrminventarioTable">Ver inventario</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/Frmentrada">
            <Link to="/dashboard/Frmentrada">Registrar entrada</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/productonuevo">
            <Link to="/dashboard/productonuevo">Nuevo producto</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/producto/lista">
            <Link to="/dashboard/producto/lista">Lista de productos</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/Frmcentrocosto">
            <Link to="/dashboard/Frmcentrocosto">Centros de costo</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/FrmAnularIngreso">
            <Link to="/dashboard/FrmAnularIngreso">Anular entrada</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/FrmAjusteInventario">
            <Link to="/dashboard/FrmAjusteInventario">Ajuste inventario</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}

      {(puedeVer('ganado') || puedeVer('leche')) && (
        <Menu.SubMenu
          key="/dashboard/ganado"
          icon={<CowfaceIcon style={{ width: 20, height: 20 }} />}
          title="Animales"
        >
          {puedeVer('ganado') && <>
            <Menu.Item key="/dashboard/ganado/animales">
              <Link to="/dashboard/ganado/animales">Lista de animales</Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/ganado/animales/nuevo">
              <Link to="/dashboard/ganado/animales/nuevo">Nuevo animal</Link>
            </Menu.Item>
          </>}
          {puedeVer('leche') && <>
            <Menu.Item key="/dashboard/ganado/leche/produccion">
              <Link to="/dashboard/ganado/leche/produccion">🐄 Vacas en producción</Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/ganado/leche/lote">
              <Link to="/dashboard/ganado/leche/lote">Registro de leche por lote</Link>
            </Menu.Item>
          </>}
          {puedeVer('reportes') && <>
            <Menu.Item key="/dashboard/ganado/leche/vaca">
              <Link to="/dashboard/ganado/leche/vaca">📊 Producción por vaca</Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/ganado/leche/estadisticas">
              <Link to="/dashboard/ganado/leche/estadisticas">Estadísticas de leche</Link>
            </Menu.Item>
          </>}
          {puedeVer('configuracion') && (
            <Menu.Item key="/dashboard/ganado/leche/config">
              <Link to="/dashboard/ganado/leche/config">💲 Precio por litro</Link>
            </Menu.Item>
          )}
        </Menu.SubMenu>
      )}

      {puedeVer('cultivos') && (
        <Menu.SubMenu
          key="/dashboard/cultivos"
          icon={<span style={{ fontSize: 18 }}>🌱</span>}
          title="Cultivos"
        >
          <Menu.Item key="/dashboard/cultivos">
            <Link to="/dashboard/cultivos">Lista de cultivos</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/cultivos/nuevo">
            <Link to="/dashboard/cultivos/nuevo">Nuevo cultivo</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/cultivos/tipos">
            <Link to="/dashboard/cultivos/tipos">⚙️ Tipos de cultivo</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}

      {puedeVer('usuarios') && (
        <Menu.SubMenu
          key="/dashboard/admin"
          icon={<UserOutlined />}
          title="Administración"
        >
          <Menu.Item key="/dashboard/usuarios">
            <Link to="/dashboard/usuarios">Lista de usuarios</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/usuarios/nuevo">
            <Link to="/dashboard/usuarios/nuevo">➕ Nuevo usuario</Link>
          </Menu.Item>
          <Menu.Item key="/dashboard/roles">
            <Link to="/dashboard/roles">🔐 Roles y permisos</Link>
          </Menu.Item>
        </Menu.SubMenu>
      )}

      <Menu.Item key="/dashboard/configuracion" icon={<SettingOutlined />}>
        <Link to="/dashboard/configuracion">Configuración</Link>
      </Menu.Item>

      <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout} danger>
        Cerrar sesión
      </Menu.Item>
    </Menu>
  );
}

// ─── Barra de navegación inferior (solo móvil) ─────────────────────────────

function BottomNav({ darkMode, onMenuOpen }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const bg   = darkMode ? '#001529' : '#fff';
  const border = darkMode ? '#1f1f1f' : '#f0f0f0';

  const active = (path) => ({
    color: pathname.startsWith(path) ? '#1677ff' : (darkMode ? '#aaa' : '#666'),
  });

  const item = {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', flex: 1, padding: '6px 0',
    fontSize: 10, fontWeight: 500, cursor: 'pointer', gap: 3,
    background: 'none', border: 'none',
  };

  const iconSize = { fontSize: 22 };

  return (
    <div style={{
      position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1000,
      display: 'flex', height: 60,
      background: bg, borderTop: `1px solid ${border}`,
      boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
    }}>
      <button style={{ ...item, ...active('/dashboard') }}
        onClick={() => navigate('/dashboard')}>
        <HomeOutlined style={iconSize} />
        <span>Inicio</span>
      </button>

      <button style={{ ...item, ...active('/dashboard/F') }}
        onClick={() => navigate('/dashboard/FrminventarioTable')}>
        <AppstoreOutlined style={iconSize} />
        <span>Inventario</span>
      </button>

      <button style={{ ...item, ...active('/dashboard/ganado') }}
        onClick={() => navigate('/dashboard/ganado/animales')}>
        <CowfaceIcon style={{ width: 22, height: 22, ...active('/dashboard/ganado') }} />
        <span style={active('/dashboard/ganado')}>Animales</span>
      </button>

      <button style={{ ...item, color: darkMode ? '#aaa' : '#666' }}
        onClick={onMenuOpen}>
        <MenuOutlined style={iconSize} />
        <span>Menú</span>
      </button>
    </div>
  );
}

// ─── Dashboard principal ────────────────────────────────────────────────────

const Dashboard = () => {
  const [collapsed, setCollapsed]   = useState(false);
  const [darkMode, setDarkMode]     = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { user } = useUser();
  const screens = useBreakpoint();

  // md = 768px+  → desktop | menor → móvil
  const isMobile = !screens.md;

  return (
    <ConfigProvider theme={{ algorithm: darkMode ? darkAlgorithm : defaultAlgorithm }}>
      <Layout style={{ minHeight: '100vh' }}>

        {/* ── Sidebar (solo desktop) ── */}
        {!isMobile && (
          <Sider
            collapsible
            collapsed={collapsed}
            onCollapse={setCollapsed}
            theme={darkMode ? 'dark' : 'light'}
            style={{ background: darkMode ? undefined : '#e6f7ff' }}
          >
            <div style={{
              height: 32, margin: 16, borderRadius: 4,
              background: darkMode ? 'rgba(255,255,255,0.3)' : 'rgba(0,63,135,0.3)',
            }} />
            <MenuItems darkMode={darkMode} />
          </Sider>
        )}

        <Layout>
          {/* ── Header ── */}
          <Header style={{
            display: 'flex', alignItems: 'center', padding: '0 16px',
            background: darkMode ? '#001529' : '#fff',
            boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
          }}>
            {/* Botón colapsar solo en desktop */}
            {!isMobile && (
              React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                onClick: () => setCollapsed(p => !p),
                style: { fontSize: 18, marginRight: 16, cursor: 'pointer',
                         color: darkMode ? '#fff' : '#000' },
              })
            )}

            <Title level={4} style={{ margin: 0, flex: 1, color: darkMode ? '#fff' : '#000' }}>
              MiGranja
            </Title>

            <Space>
              <Switch
                checked={darkMode}
                onChange={setDarkMode}
                checkedChildren="🌙"
                unCheckedChildren="☀️"
                size="small"
              />
              {user && !isMobile && (
                <Space size={6}>
                  <UserOutlined style={{ color: darkMode ? '#fff' : '#000' }} />
                  <Text style={{ color: darkMode ? '#fff' : '#000' }}>
                    {user.nombreUsuario}
                  </Text>
                </Space>
              )}
            </Space>
          </Header>

          {/* ── Contenido ── */}
          <Content style={{
            margin: isMobile ? '8px 8px 68px' : '16px',
            padding: isMobile ? 12 : 16,
            background: darkMode ? '#141414' : '#fff',
            borderRadius: 8,
            minHeight: 280,
          }}>
            <Outlet />
          </Content>
        </Layout>

        {/* ── Bottom Nav (solo móvil) ── */}
        {isMobile && (
          <BottomNav
            darkMode={darkMode}
            onMenuOpen={() => setDrawerOpen(true)}
          />
        )}

        {/* ── Drawer menú completo (móvil) ── */}
        <Drawer
          title="Menú"
          placement="bottom"
          height="auto"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          styles={{ body: { padding: 0 } }}
        >
          <MenuItems
            darkMode={darkMode}
            onSelect={() => setDrawerOpen(false)}
          />
        </Drawer>

      </Layout>
    </ConfigProvider>
  );
};

export default Dashboard;
